import React from "react";

import { isGUID } from "@bps/common-utils";
import { PresetButtonType, useTheme } from "@bps/fluent-ui";
import { Validator } from "@bps/validator";
import { FilterBar } from "@components/filter-bar/FilterBar";
import { Item } from "@components/filter-bar/FilterBar.types";
import { BpIdUsersSearchArgs } from "@libs/api/gateways/bp-id/bp-id.dtos";
import { ValidationMessages } from "@libs/validation/validation-messages.enum";

export type PatientSearchFilterValues = Partial<BpIdUsersSearchArgs> & {
  bhcaSearch?: boolean;
};

interface IdentityFilterProps {
  onFilter: (values: PatientSearchFilterValues) => void;
}

const validator = new Validator<PatientSearchFilterValues>(validator => ({
  email: validator.string().email({
    message: ValidationMessages.email
  }),
  mobile: validator.string().phone({ message: "Numbers only" }),
  objectId: validator.custom().func<"objectId">(value => {
    if (!value) return undefined;
    return isGUID(value) ? undefined : ValidationMessages.guid;
  })
}));

export const IdentityFilter: React.FC<IdentityFilterProps> = ({ onFilter }) => {
  const theme = useTheme();

  const items: Item<PatientSearchFilterValues>[] = [
    {
      name: "email",
      type: "searchBox",
      stickItem: true,
      props: { placeholder: "Search email", removeSpecialCharacters: false }
    },
    {
      name: "name",
      type: "searchBox",
      props: { placeholder: "Search name", removeSpecialCharacters: false }
    },
    {
      name: "mobile",
      type: "searchBox",
      props: { placeholder: "Search mobile", removeSpecialCharacters: false }
    },
    {
      name: "profileIdPrefix",
      type: "searchBox",
      props: {
        placeholder: "Search profile ID",
        removeSpecialCharacters: false
      },
      disabledCondition: values => !values.bhcaSearch
    },
    {
      name: "objectId",
      type: "searchBox",
      props: {
        placeholder: "Search Object ID",
        removeSpecialCharacters: false
      }
    }
  ];

  const presets: PresetButtonType<PatientSearchFilterValues>[] = [
    {
      name: "bhcaSearch",
      iconName: "CheckMark",
      iconColor: theme.semanticColors.successIcon,
      text: "BHCa search off",
      textPressed: "BHCa search on"
    }
  ];

  return (
    <FilterBar
      items={items}
      presets={presets}
      validate={validator.validate}
      initialValues={{ bhcaSearch: true }}
      onChange={({ state: { values } }) => onFilter(values)}
      maxItemWidth={188}
    />
  );
};
